import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styles from './ProjectUpsell.module.scss';

const FALLBACK_ROLES_BY_MEDIA = {
  audio: ['vocalist', 'dj', 'musicproducer', 'musician'],
  video: ['filmproducer', 'videoeditor', 'cameraoperator', 'postproduction'],
  default: ['graphicdesigner', 'illustrator', 'imageeditor', 'uxdesigner'],
};

/**
 * @param {{ slug: string, slug_plural: string, name: string, name_plural: string }} role
 * @param {string} tag
 * @returns {boolean}
 */
const roleMatchesTag = (role, tag) =>
  role.slug.replace(/-/g, '') === tag ||
  role.slug_plural.replace(/-/g, '') === tag ||
  role.name === tag ||
  role.name_plural === tag;

/**
 * @param {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>} suggestedRoles
 * @param {string} tag
 * @returns {{ slug: string, slug_plural: string, name: string, name_plural: string }|undefined}
 */
const findRoleByTag = (suggestedRoles, tag) =>
  suggestedRoles.find(
    (role) => typeof role !== 'undefined' && roleMatchesTag(role, tag)
  );

/**
 * Match credited role tags to suggested role records, preserving order and
 * dropping duplicates by slug_plural.
 *
 * @param {Array<{ roles?: string[] }>} credits
 * @param {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>} suggestedRoles
 * @returns {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>}
 */
const matchRolesFromCredits = (credits, suggestedRoles) => {
  const matchedRoles = [];
  const seen = new Set();

  (credits || []).forEach((credit) => {
    (credit.roles || []).forEach((tag) => {
      const role = findRoleByTag(suggestedRoles, tag);
      if (role && !seen.has(role.slug_plural)) {
        seen.add(role.slug_plural);
        matchedRoles.push(role);
      }
    });
  });

  return matchedRoles;
};

/**
 * Pick the first fallback role for the project's media type.
 *
 * @param {{ mediaType?: string }} project
 * @param {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>} suggestedRoles
 * @returns {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>}
 */
const pickFallbackRoles = (project, suggestedRoles) => {
  const fallbackTags =
    FALLBACK_ROLES_BY_MEDIA[project.mediaType] ||
    FALLBACK_ROLES_BY_MEDIA.default;

  for (let i = 0; i < fallbackTags.length; i++) {
    const role = findRoleByTag(suggestedRoles, fallbackTags[i]);
    if (role) {
      return [role];
    }
  }

  return [];
};

/**
 * Roles to promote for talent upsell: credited matches first, else media fallbacks.
 *
 * @param {object} project
 * @param {Array<{ roles?: string[] }>} credits
 * @param {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>} suggestedRoles
 * @returns {Array<{ slug: string, slug_plural: string, name: string, name_plural: string }>}
 */
const getTalentRoles = (project, credits, suggestedRoles) => {
  if (!project || !suggestedRoles || suggestedRoles.length === 0) {
    return [];
  }

  const matchedRoles = matchRolesFromCredits(credits, suggestedRoles);
  if (matchedRoles.length > 0) {
    return matchedRoles;
  }

  return pickFallbackRoles(project, suggestedRoles);
};

const ProjectCreativeUpsell = ({ project, credits, suggestedRoles }) => {
  const talentRoles = getTalentRoles(project, credits, suggestedRoles);

  if (talentRoles.length === 0) {
    return null;
  }

  return (
    <div className={styles.upsellContainer}>
      <div className={styles.upsellTitle}>
        <Link to="/jobs">Find jobs</Link>
      </div>
      <ul>
        {talentRoles.map((role) => (
          <li key={role.slug_plural}>
            <Link
              className={styles.upsellItem}
              to={`/jobs/${role.slug_plural}`}
            >
              {role.name} Jobs
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

ProjectCreativeUpsell.propTypes = {
  project: PropTypes.object,
  credits: PropTypes.array,
  suggestedRoles: PropTypes.array,
};

export default ProjectCreativeUpsell;
